import api from "@/app/api";
import Guid from "@/app/types/common/guid";
import { Instant, LocalDate, LocalTime } from "@js-joda/core";
import {ModelObject, ModelObjectWithDirtyCheck, type ValidationRule} from "@/app/services/modelObject";
import ServiceBase from "@/app/services/serviceBase";
import converters from "@/app/services/converters"
import { EnumCurrency } from "./enums/enumCurrency.generated";
import { EnumEcommerceOrderOriginType } from "./enums/enumEcommerceOrderOriginType.generated";

// Model Objects

export class EcommerceOrder extends ModelObject<EcommerceOrder> {

    public version: number = 0;
    /**
    * The origin type of this order.
    */
    public originType: EnumEcommerceOrderOriginType = EnumEcommerceOrderOriginType.none;
    /**
    * The identifier of the store associated with this order.
    * Not Nullable
    * Preserves leading and trailing whitespace.
    */
    public originStoreIdentifier: string = "";
    /**
    * The unique identifier for this order as given by the originating service.
    * Not Nullable
    * Preserves leading and trailing whitespace.
    */
    public originOrderIdentifier: string = "";
    /**
    * The order name (visible friendly name shown to customers) as given by the originating service.
    * Not Nullable
    * Preserves leading and trailing whitespace.
    */
    public originOrderName: string = "";
    /**
    * The date and time this order was created in Ecommerce.
    */
    public createdAt: Instant = Instant.ofEpochMilli(0);
    /**
    * The total number of items in the order.
    */
    public totalOrderQuantity: number = 0;
    /**
    * The total net value of the order including delivery.
    */
    public totalNetPrice: number = 0;
    private _brewmanOrderId: Guid | null = null;
    /**
    * The Brewman order id assigned to this order on a successful import. If empty, check the error message field for details.
    */
    public get brewmanOrderId(): Guid | null { return this._brewmanOrderId }
    public set brewmanOrderId(value: Guid | null) {
      this._brewmanOrderId = value === undefined ? null : value;
    }
    /**
    * Error information associated with a failed import.
    * Not Nullable
    * Preserves leading and trailing whitespace.
    */
    public errorMessage: string = "";
    /**
    * The currency code for the order's total net price.
    */
    public currencyCode: EnumCurrency = EnumCurrency.none;

    public constructor(jsonSerializedObject?: any) {
        super();
        if (jsonSerializedObject) {
          this.version = jsonSerializedObject.version;
          this.originType = EnumEcommerceOrderOriginType.getByValue(jsonSerializedObject.originType, this.originType);
          this.originStoreIdentifier = jsonSerializedObject.originStoreIdentifier;
          this.originOrderIdentifier = jsonSerializedObject.originOrderIdentifier;
          this.originOrderName = jsonSerializedObject.originOrderName;
          this.createdAt = Instant.parse(jsonSerializedObject.createdAt);
          this.totalOrderQuantity = jsonSerializedObject.totalOrderQuantity;
          this.totalNetPrice = jsonSerializedObject.totalNetPrice;
          this.brewmanOrderId = jsonSerializedObject.brewmanOrderId == null ? null : Guid.parseOrEmpty(jsonSerializedObject.brewmanOrderId);
          this.errorMessage = jsonSerializedObject.errorMessage;
          this.currencyCode = EnumCurrency.getByValue(jsonSerializedObject.currencyCode, this.currencyCode);
        }

    }

    public toJsonSerializedObject(): any {
        const toRet = {} as any;
        toRet.version = this.version;
        toRet.originType = this.originType.value;
        toRet.originStoreIdentifier = this.originStoreIdentifier;
        toRet.originOrderIdentifier = this.originOrderIdentifier;
        toRet.originOrderName = this.originOrderName;
        toRet.createdAt = this.createdAt.toString();
        toRet.totalOrderQuantity = this.totalOrderQuantity;
        toRet.totalNetPrice = this.totalNetPrice;
        toRet.brewmanOrderId = this.brewmanOrderId == null ? null : this.brewmanOrderId.toString();
        toRet.errorMessage = this.errorMessage;
        toRet.currencyCode = this.currencyCode.value;
        return toRet;
    }

    public toProto(): any {
      return this.toJsonSerializedObject();
    }

    public toBinary(): Uint8Array {
      return new TextEncoder().encode(JSON.stringify(this.toJsonSerializedObject()));
    }

    public validationRules: ValidationRule = [
            {field: "version", rule: (v: any) => v == null || v >= -9223372036854775808 || "Version is not greater than minimum allowed value: -9223372036854775808"},
            {field: "version", rule: (v: any) => v == null || v <= 9223372036854775807 || "Version is above maximum allowed value: 9223372036854775807"},
            {field: "brewmanOrderId", rule: (v: any) => v == null || !v.isEmpty() || "EcommerceOrder.BrewmanOrderId cannot be empty"},
    ];
}


export class FilteredEcommerceOrdersResult extends ModelObject<FilteredEcommerceOrdersResult> {

    public matchingCount: number = 0;
    public ecommerceOrders: (EcommerceOrder)[] = [];

    public constructor(jsonSerializedObject?: any) {
        super();
        if (jsonSerializedObject) {
          this.matchingCount = jsonSerializedObject.matchingCount;
          this.ecommerceOrders = jsonSerializedObject.ecommerceOrders.map((v: any) => new EcommerceOrder(v));
        }

    }

    public toJsonSerializedObject(): any {
        const toRet = {} as any;
        toRet.matchingCount = this.matchingCount;
        toRet.ecommerceOrders = this.ecommerceOrders.map(v => v.toJsonSerializedObject());
        return toRet;
    }

    public toProto(): any {
      return this.toJsonSerializedObject();
    }

    public toBinary(): Uint8Array {
      return new TextEncoder().encode(JSON.stringify(this.toJsonSerializedObject()));
    }

    public validationRules: ValidationRule = [
            {field: "matchingCount", rule: (v: any) => v == null || v >= -2147483648 || "MatchingCount is not greater than minimum allowed value: -2147483648"},
            {field: "matchingCount", rule: (v: any) => v == null || v <= 2147483647 || "MatchingCount is above maximum allowed value: 2147483647"},
            {field: "ecommerceOrders", rule: (v: any) => !!v || "ecommerceOrders is required"},
            {field: "ecommerceOrders", rule: (v: any) => {
                if (v === null) {
                    return true;
                }

                return (v as EcommerceOrder[]).reduce((acc: Array<string>, curr: EcommerceOrder) => acc.concat(curr.validate()), new Array<string>());
            }},
    ];
}


export class EcommerceOrderFilter extends ModelObject<EcommerceOrderFilter> {

    private _maximumResults: number | null = null;
    public get maximumResults(): number | null { return this._maximumResults }
    public set maximumResults(value: number | null) {
      this._maximumResults = value === undefined ? null : value;
    }
    private _createdFromDate: LocalDate | null = null;
    /**
    * Minimum (inclusive) created at date
    */
    public get createdFromDate(): LocalDate | null { return this._createdFromDate }
    public set createdFromDate(value: LocalDate | null) {
      this._createdFromDate = value === undefined ? null : value;
    }
    private _createdToDate: LocalDate | null = null;
    /**
    * Maximum (inclusive) created at date
    */
    public get createdToDate(): LocalDate | null { return this._createdToDate }
    public set createdToDate(value: LocalDate | null) {
      this._createdToDate = value === undefined ? null : value;
    }
    private _ecommerceOriginTypes: readonly (EnumEcommerceOrderOriginType)[] | null = null;
    /**
    * Limit to specified ecommerce origin types
    */
    public get ecommerceOriginTypes(): readonly (EnumEcommerceOrderOriginType)[] | null { return this._ecommerceOriginTypes }
    public set ecommerceOriginTypes(value: readonly (EnumEcommerceOrderOriginType)[] | null) {
      this._ecommerceOriginTypes = value === undefined ? null : value;
    }
    private _ecommerceStoreIdentifiers: readonly (string)[] | null = null;
    /**
    * Limit to specified ecommerce store identifiers
    */
    public get ecommerceStoreIdentifiers(): readonly (string)[] | null { return this._ecommerceStoreIdentifiers }
    public set ecommerceStoreIdentifiers(value: readonly (string)[] | null) {
      this._ecommerceStoreIdentifiers = value === undefined ? null : value;
    }
    private _ecommerceOrderIdentifiers: readonly (string)[] | null = null;
    /**
    * Limit to just orders with one of the given order identifiers.
    */
    public get ecommerceOrderIdentifiers(): readonly (string)[] | null { return this._ecommerceOrderIdentifiers }
    public set ecommerceOrderIdentifiers(value: readonly (string)[] | null) {
      this._ecommerceOrderIdentifiers = value === undefined ? null : value;
    }
    private _ecommerceOrderNames: readonly (string)[] | null = null;
    /**
    * Limit to just orders with one of the given order names.
    */
    public get ecommerceOrderNames(): readonly (string)[] | null { return this._ecommerceOrderNames }
    public set ecommerceOrderNames(value: readonly (string)[] | null) {
      this._ecommerceOrderNames = value === undefined ? null : value;
    }

    public constructor(jsonSerializedObject?: any) {
        super();
        if (jsonSerializedObject) {
          this.maximumResults = jsonSerializedObject.maximumResults == null ? null : jsonSerializedObject.maximumResults;
          this.createdFromDate = jsonSerializedObject.createdFromDate == null ? null : LocalDate.parse(jsonSerializedObject.createdFromDate);
          this.createdToDate = jsonSerializedObject.createdToDate == null ? null : LocalDate.parse(jsonSerializedObject.createdToDate);
          this.ecommerceOriginTypes = jsonSerializedObject.ecommerceOriginTypes == null ? null : jsonSerializedObject.ecommerceOriginTypes.map((v: any) => EnumEcommerceOrderOriginType.getByValue(v));
          this.ecommerceStoreIdentifiers = jsonSerializedObject.ecommerceStoreIdentifiers == null ? null : jsonSerializedObject.ecommerceStoreIdentifiers.map((v: any) => v);
          this.ecommerceOrderIdentifiers = jsonSerializedObject.ecommerceOrderIdentifiers == null ? null : jsonSerializedObject.ecommerceOrderIdentifiers.map((v: any) => v);
          this.ecommerceOrderNames = jsonSerializedObject.ecommerceOrderNames == null ? null : jsonSerializedObject.ecommerceOrderNames.map((v: any) => v);
        }

    }

    public toJsonSerializedObject(): any {
        const toRet = {} as any;
        toRet.maximumResults = this.maximumResults == null ? null : this.maximumResults;
        toRet.createdFromDate = this.createdFromDate == null ? null : this.createdFromDate.toString();
        toRet.createdToDate = this.createdToDate == null ? null : this.createdToDate.toString();
        toRet.ecommerceOriginTypes = this.ecommerceOriginTypes == null ? null : this.ecommerceOriginTypes.map(v => v.value);
        toRet.ecommerceStoreIdentifiers = this.ecommerceStoreIdentifiers == null ? null : this.ecommerceStoreIdentifiers.map(v => v);
        toRet.ecommerceOrderIdentifiers = this.ecommerceOrderIdentifiers == null ? null : this.ecommerceOrderIdentifiers.map(v => v);
        toRet.ecommerceOrderNames = this.ecommerceOrderNames == null ? null : this.ecommerceOrderNames.map(v => v);
        return toRet;
    }

    public toProto(): any {
      return this.toJsonSerializedObject();
    }

    public toBinary(): Uint8Array {
      return new TextEncoder().encode(JSON.stringify(this.toJsonSerializedObject()));
    }

    public validationRules: ValidationRule = [
            {field: "maximumResults", rule: (v: any) => v == null || v >= 0 || "MaximumResults is not greater than minimum allowed value: 0"},
            {field: "maximumResults", rule: (v: any) => v == null || v <= 2147483647 || "MaximumResults is above maximum allowed value: 2147483647"},
    ];
}


export class EcommerceOrderService extends ServiceBase {

  public async getEcommerceOrdersByFilter(filter: EcommerceOrderFilter, opts?: {requestTimeoutMs?: number, sessionToken?: string}): Promise<FilteredEcommerceOrdersResult> {
    const req = {
    tenantId:api.tenantId.toString(),
    filter:filter.toJsonSerializedObject(),
    };
    const extractor = (response:any) => new FilteredEcommerceOrdersResult(response.results);;
    return this.makeJsonRequest("ecommerceOrder/v1/getEcommerceOrdersByFilter", req, false, extractor, opts);
  };
}

export const ecommerceOrderService = new EcommerceOrderService();